import {
  DndContext,
  type DragEndEvent,
  type DragMoveEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers';
import type { Modifier } from '@dnd-kit/core';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { calendarAutoScrollOptions } from './auto-scroll';
import * as styles from './calendar-scheduler.css';
import { EventItem } from './event-item';
import type { CalendarEvent, EventRenderer, EventResizeHandler } from './types';
import {
  calculateEventLayouts,
  formatDateKey,
  type EventLayout,
} from './utils';

interface WeekEventLayerProps {
  events: CalendarEvent[];
  weekDates: Date[];
  onEventEdit: (event: CalendarEvent) => void;
  onEventUpdate: (event: CalendarEvent) => void;
  onEventDelete: (id: string) => void;
  renderEvent?: EventRenderer;
  dragSnapMinutes?: number;
}

export const WeekEventLayer: React.FC<WeekEventLayerProps> = ({
  events,
  weekDates,
  onEventEdit,
  onEventUpdate,
  onEventDelete: _onEventDelete,
  renderEvent,
  dragSnapMinutes = 15,
}) => {
  const [activeEvent, setActiveEvent] = useState<CalendarEvent | null>(null);
  const [activeEventDimensions, setActiveEventDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const dragAxisRef = useRef<'x' | 'y' | null>(null);
  
  // Optimistic update support
  const committedEventRef = useRef<CalendarEvent | null>(null);
  const [updateTick, forceUpdate] = useState({});

  // Merge props with committed local state
  const mergedEvents = useMemo(() => {
    const committed = committedEventRef.current;
    if (!committed) return events;

    const propEvent = events.find(e => e.id === committed.id);
    if (propEvent && propEvent.startTime === committed.startTime && propEvent.endTime === committed.endTime && propEvent.date === committed.date) {
      committedEventRef.current = null;
      return events;
    }

    return events.map(e => (e.id === committed.id ? { ...e, ...committed } : e));
  }, [events, updateTick]);

  const weekDateKeys = useMemo(
    () => weekDates.map(date => formatDateKey(date)),
    [weekDates]
  );

  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    weekDateKeys.forEach(key => map.set(key, []));
    mergedEvents.forEach(event => {
      const bucket = map.get(event.date);
      if (bucket) {
        bucket.push(event);
      }
    });
    return map;
  }, [mergedEvents, weekDateKeys]);

  const layoutsByDate = useMemo(() => {
    const map = new Map<string, Map<string, EventLayout>>();
    for (const [key, dayEvents] of eventsByDate.entries()) {
      const layouts = calculateEventLayouts(dayEvents);
      const layoutMap = new Map<string, EventLayout>();
      layouts.forEach(layout => layoutMap.set(layout.event.id, layout));
      map.set(key, layoutMap);
    }
    return map;
  }, [eventsByDate]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const getDraggedEventUpdate = useCallback(
    (eventData: CalendarEvent, delta: DragMoveEvent['delta']) => {
      if (!layerRef.current) return null;
      const layerRect = layerRef.current.getBoundingClientRect();
      const totalHeight = layerRect.height;
      if (totalHeight <= 0) return null;

      const totalMinutes = 24 * 60;
      const snapInterval =
        Number.isFinite(dragSnapMinutes) && dragSnapMinutes > 0
          ? dragSnapMinutes
          : 15;

      const startParts = eventData.startTime.split(':').map(Number);
      const endParts = eventData.endTime.split(':').map(Number);
      const startHour = startParts[0];
      const startMinute = startParts[1];
      const endHour = endParts[0];
      const endMinute = endParts[1];
      if (
        startHour === undefined ||
        startMinute === undefined ||
        endHour === undefined ||
        endMinute === undefined ||
        !Number.isFinite(startHour) ||
        !Number.isFinite(startMinute) ||
        !Number.isFinite(endHour) ||
        !Number.isFinite(endMinute)
      ) {
        return null;
      }

      const durationMinutes =
        endHour * 60 + endMinute - (startHour * 60 + startMinute);

      const dragMinutes = (delta.y / totalHeight) * totalMinutes;
      const startMinutes = startHour * 60 + startMinute;
      const snappedStartMinutes =
        Math.round((startMinutes + dragMinutes) / snapInterval) * snapInterval;

      let newStartMinutes = snappedStartMinutes;
      if (newStartMinutes < 0) {
        newStartMinutes = 0;
      }
      if (newStartMinutes + durationMinutes > totalMinutes) {
        newStartMinutes = totalMinutes - durationMinutes;
      }

      const columnWidth = layerRect.width / 7;
      const dayShift =
        columnWidth > 0 ? Math.round(delta.x / columnWidth) : 0;
      const currentDayIndex = weekDateKeys.indexOf(eventData.date);
      const nextDayIndex =
        currentDayIndex === -1
          ? 0
          : Math.max(0, Math.min(6, currentDayIndex + dayShift));
      const nextDateKey = weekDateKeys[nextDayIndex] ?? eventData.date;

      const newStartHour = Math.floor(newStartMinutes / 60);
      const newStartMin = newStartMinutes % 60;
      const newEndMinutes = newStartMinutes + durationMinutes;
      const newEndHour = Math.floor(newEndMinutes / 60) % 24;
      const newEndMin = newEndMinutes % 60;

      const newStartTime = `${newStartHour.toString().padStart(2, '0')}:${newStartMin.toString().padStart(2, '0')}`;
      const newEndTime = `${newEndHour.toString().padStart(2, '0')}:${newEndMin.toString().padStart(2, '0')}`;

      if (
        newStartTime === eventData.startTime &&
        newEndTime === eventData.endTime &&
        nextDateKey === eventData.date
      ) {
        return null;
      }

      return { startTime: newStartTime, endTime: newEndTime, date: nextDateKey };
    },
    [dragSnapMinutes, weekDateKeys]
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const eventData = event.active.data.current?.['event'] as
      | CalendarEvent
      | undefined;
    dragAxisRef.current = null;
    if (eventData) {
      setActiveEvent(eventData);
      const element = layerRef.current?.querySelector(
        `[data-event-id="${eventData.id}"]`
      ) as HTMLElement | null;
      if (element) {
        const rect = element.getBoundingClientRect();
        setActiveEventDimensions({ width: rect.width, height: rect.height });
      }
    }
  }, []);

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      const eventData = event.active.data.current?.['event'] as
        | CalendarEvent
        | undefined;
      if (!eventData) return;
      const update = getDraggedEventUpdate(eventData, event.delta);
      if (!update) return;
      setActiveEvent(prev => {
        if (
          prev &&
          prev.startTime === update.startTime &&
          prev.endTime === update.endTime &&
          prev.date === update.date
        ) {
          return prev;
        }
        return { ...eventData, ...update };
      });
    },
    [getDraggedEventUpdate]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, delta } = event;
      
      dragAxisRef.current = null;

      if (!layerRef.current) {
        setActiveEvent(null);
        setActiveEventDimensions(null);
        return;
      }

      const eventData = active.data.current?.['event'] as CalendarEvent | undefined;
      if (!eventData) {
        setActiveEvent(null);
        setActiveEventDimensions(null);
        return;
      }

      const update = getDraggedEventUpdate(eventData, delta);
      if (!update) {
        setActiveEvent(null);
        setActiveEventDimensions(null);
        return;
      }

      // Optimistic update
      const updatedEvent = { ...eventData, ...update };
      committedEventRef.current = updatedEvent;
      forceUpdate({});

      setActiveEvent(null);
      setActiveEventDimensions(null);

      onEventUpdate(updatedEvent);
    },
    [getDraggedEventUpdate, onEventUpdate]
  );

  const handleEventResize: EventResizeHandler = useCallback(
    (event: CalendarEvent, newEndTime: string) => {
      onEventUpdate({
        ...event,
        endTime: newEndTime,
      });
    },
    [onEventUpdate]
  );

  const axisLockModifier: Modifier = ({ transform }) => {
    const threshold = 8;
    const absX = Math.abs(transform.x);
    const absY = Math.abs(transform.y);

    if (!dragAxisRef.current) {
      if (absX < threshold && absY < threshold) {
        return transform;
      }
      dragAxisRef.current = absX > absY ? 'x' : 'y';
    }

    if (dragAxisRef.current === 'x') {
      return { ...transform, y: 0 };
    }
    if (dragAxisRef.current === 'y') {
      return { ...transform, x: 0 };
    }
    return transform;
  };

  return (
    <DndContext
      sensors={sensors}
      autoScroll={calendarAutoScrollOptions}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={() => {
        dragAxisRef.current = null;
      }}
      modifiers={[axisLockModifier, restrictToFirstScrollableAncestor]}
    >
      <div ref={layerRef} className={styles.weekEventLayer}>
        {weekDateKeys.map(dateKey => {
          const dayEvents = eventsByDate.get(dateKey) ?? [];
          const layoutMap = layoutsByDate.get(dateKey);
          return (
            <div key={dateKey} className={styles.weekEventColumn}>
              {dayEvents.map(event => {
                const layout = layoutMap?.get(event.id);
                return (
                  <EventItem
                    key={event.id}
                    event={event}
                    onEdit={onEventEdit}
                    onResize={handleEventResize}
                    renderEvent={renderEvent}
                    column={layout?.column ?? 0}
                    totalColumns={layout?.totalColumns ?? 1}
                  />
                );
              })}
            </div>
          );
        })}
      </div>

      <DragOverlay>
        {activeEvent ? (
          <div
            style={{
              opacity: 1,
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
              cursor: 'grabbing',
              backgroundColor: renderEvent
                ? 'transparent'
                : 'var(--affine-background-primary-color)',
              borderRadius: '8px',
              width: activeEventDimensions?.width
                ? `${activeEventDimensions.width}px`
                : renderEvent
                  ? '260px'
                  : '200px',
              height: activeEventDimensions?.height
                ? `${activeEventDimensions.height}px`
                : (() => {
                    const startParts = activeEvent.startTime.split(':').map(Number);
                    const endParts = activeEvent.endTime.split(':').map(Number);
                    const startHour = startParts[0] ?? 0;
                    const startMinute = startParts[1] ?? 0;
                    const endHour = endParts[0] ?? 0;
                    const endMinute = endParts[1] ?? 0;
                    const startMinutes = startHour * 60 + startMinute;
                    const endMinutes = endHour * 60 + endMinute;
                    const durationMinutes = endMinutes - startMinutes;
                    const layerHeight =
                      layerRef.current?.getBoundingClientRect().height ||
                      24 * 40;
                    return `${(durationMinutes / (24 * 60)) * layerHeight}px`;
                  })(),
              minHeight: '16px',
              overflow: 'hidden',
            }}
          >
            <EventItem
              event={activeEvent}
              onEdit={() => {}}
              onResize={() => {}}
              isDragOverlay={true}
              renderEvent={renderEvent}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
